import { Fragment, useMemo, useState, useRef } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import SelectBox from "components/elements/selectBox";
import "react-datepicker/dist/react-datepicker.css";
import JoditEditor from "jodit-react";

const ALLOWED_CHARS = /^[0-9+\-*/(). %current]+$/;

function evaluateFormula(formula, current) {
    if (!formula) return null;
    const normalized = formula.replace(/\s+/g, " ").trim();

    if (!ALLOWED_CHARS.test(normalized)) return null;

    let expr = normalized.replace(/%/g, "/100");
    expr = expr.replace(/\bcurrent\b/g, "__x");

    if (/[A-Za-z]/.test(expr.replace(/__x/g, ""))) return null;

    try {
        // eslint-disable-next-line no-new-func
        const fn = new Function("__x", `"use strict"; return (${expr});`);
        const result = fn(current);
        if (typeof result !== "number" || Number.isNaN(result) || !Number.isFinite(result)) return null;
        return result;
    } catch {
        return null;
    }
}

function ModalUpload({ onSubmit, templates, products_selected }) {
    const MAX_PRODUCT_FEATURE = 5;
    const MAX_TAGS = 13;

    const [templateId, setTeamplateId] = useState(null);
    const [option_change, setOptionChange] = useState("stock_custom");
    const [warehouse_type, setWarehouseType] = useState("default");
    const [type_change, setTypeChange] = useState("fix");
    const [fixedValue, setFixedValue] = useState("");
    const [description, setDescription] = useState("");
    const [formula, setFormula] = useState("current * 1.10");
    const [previewCurrent, setPreviewCurrent] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    // title_change
    const [oldTitle, setOldTitle] = useState("");
    const [newTitle, setNewTitle] = useState("");

    // package_change
    const [lengthValue, setLengthValue] = useState("");
    const [widthValue, setWidthValue] = useState("");
    const [heightValue, setHeightValue] = useState("");
    const [dimensionUnit, setDimensionUnit] = useState("CENTIMETER"); // CENTIMETER | INCH
    const [weightValue, setWeightValue] = useState("");
    const [weightUnit, setWeightUnit] = useState("KILOGRAM"); // KILOGRAM | POUND
    const [mainImages, setMainImages] = useState(""); // NEW
    const [productFeatures, setProductFeatures] = useState([]);
    const [productFeatureInput, setProductFeatureInput] = useState("");
    const [tags, setTags] = useState([]);
    const [tagsInput, setTagsInput] = useState("");

    const { t } = useTranslation();
    const editor = useRef(null);

    const config = useMemo(
        () => ({
            placeholder: "Product description",
            height: "500px",
        }),
        []
    );

    const onClose = () => {
        setIsOpen(false);
    };

    const addValueToList = (rawValue, list, setList, max) => {
        const pieces = String(rawValue || "")
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean);

        if (!pieces.length || list.length >= max) return;

        const nextList = [...list];
        for (const item of pieces) {
            if (nextList.length >= max) break;
            if (nextList.includes(item)) continue;
            nextList.push(item);
        }

        setList(nextList);
    };

    const removeValueFromList = (index, list, setList) => {
        setList(list.filter((_, i) => i !== index));
    };

    const parsedCurrent = useMemo(() => {
        const n = Number(previewCurrent);
        return Number.isFinite(n) ? n : NaN;
    }, [previewCurrent]);

    const preview = useMemo(() => {
        if (!(option_change === "stock_custom" || option_change === "price_custom")) {
            return { ok: true, value: null };
        }
        if (!Number.isFinite(parsedCurrent)) return { ok: false, value: null };
        if (type_change === "fix") {
            const n = Number(fixedValue);
            return { ok: Number.isFinite(n), value: Number.isFinite(n) ? n : null };
        }
        const v = evaluateFormula(formula, parsedCurrent);
        return { ok: v !== null, value: v };
    }, [option_change, type_change, fixedValue, formula, parsedCurrent]);

    // sync unit: CENTIMETER <-> KILOGRAM, INCH <-> POUND
    const handleDimensionUnitChange = (value) => {
        setDimensionUnit(value);
        if (value === "CENTIMETER") {
            setWeightUnit("KILOGRAM");
        } else {
            setWeightUnit("POUND");
        }
    };

    const handleWeightUnitChange = (value) => {
        setWeightUnit(value);
        if (value === "KILOGRAM") {
            setDimensionUnit("CENTIMETER");
        } else {
            setDimensionUnit("INCH");
        }
    };

    const errors = [];
    const isCustom =
        option_change === "stock_custom" ||
        option_change === "price_custom" ||
        option_change === "main_images" ||
        option_change === "description" ||
        option_change === "description_hashtag" ||
        option_change === "attribute_clear" ||
        option_change === "title_change" ||
        option_change === "package_change" ||
        option_change === "feature_tags_change";

    if (isCustom) {
        if (option_change === "stock_custom" || option_change === "price_custom" || option_change === "attribute_clear") {
            if (type_change === "fix") {
                if (fixedValue === "") errors.push(t("Please enter a fixed value."));
                const n = Number(fixedValue);
                if (fixedValue !== "" && !Number.isFinite(n)) errors.push(t("Fixed value is invalid."));
                if (Number.isFinite(n) && n < 0) errors.push(t("Value must be non-negative."));
            } else {
                if (!formula || !formula.trim()) errors.push(t("Please enter a recipe (formula)."));
                const normalized = (formula || "").replace(/\s+/g, " ").trim();
                if (normalized && !ALLOWED_CHARS.test(normalized)) {
                    errors.push(t("Recipe contains invalid characters."));
                }
            }
        }

        // title_change validate
        if (option_change === "title_change") {
            if (!oldTitle.trim()) {
                errors.push(t("Please enter the old title."));
            }
        }

        // package_change validate
        if (option_change === "package_change") {
            const l = Number(lengthValue);
            const w = Number(widthValue);
            const h = Number(heightValue);
            const wt = Number(weightValue);

            if (!lengthValue || !widthValue || !heightValue || !weightValue) {
                errors.push(t("Please enter all dimensions and weight."));
            }

            if ((lengthValue && (!Number.isFinite(l) || l <= 0)) ||
                (widthValue && (!Number.isFinite(w) || w <= 0)) ||
                (heightValue && (!Number.isFinite(h) || h <= 0))) {
                errors.push(t("Dimensions must be positive numbers."));
            }

            if (weightValue && (!Number.isFinite(wt) || wt <= 0)) {
                errors.push(t("Weight must be a positive number."));
            }

            // extra safety: unit pair
            if (dimensionUnit === "CENTIMETER" && weightUnit !== "KILOGRAM") {
                errors.push(t("CENTIMETER must go with KILOGRAM."));
            }
            if (dimensionUnit === "INCH" && weightUnit !== "POUND") {
                errors.push(t("INCH must go with POUND."));
            }
        }

        if (option_change === "feature_tags_change") {
            if (productFeatures.length > MAX_PRODUCT_FEATURE) {
                errors.push(t(`Product Feature allows up to ${MAX_PRODUCT_FEATURE} items.`));
            }
            if (tags.length > MAX_TAGS) {
                errors.push(t(`Tags allows up to ${MAX_TAGS} items.`));
            }
        }
    } else {
        if (!templateId) errors.push(t("Please select a template."));
    }

    const canSave = errors.length === 0;

    const handleSubmit = () => {
        if (!canSave) return;

        // ===== MAIN IMAGES (KHÔNG BẮT BUỘC) =====
        if (option_change === "main_images") {
            const images = mainImages
                .split("\n")
                .map(i => i.trim())
                .filter(Boolean);

            onSubmit({
                kind: "custom",
                field: "main_images",
                mode: "fixed",
                value: images, // [] nếu không nhập
            });

            onClose();
            return;
        } else if (option_change === "description" || option_change === "description_hashtag") {
            onSubmit({
                kind: "custom",
                field: option_change,
                value: description,
                mode: "fixed",
                warehouse_type,
            });
            onClose();
            return;
        } else if (option_change === "price_custom" || option_change === "stock_custom") {
            const field = option_change === "price_custom" ? "price" : "stock";
            if (type_change === "fix") {
                onSubmit({
                    kind: "custom",
                    field,
                    mode: "fixed",
                    value: Number(fixedValue),
                    warehouse_type,
                });
            } else {
                onSubmit({
                    kind: "custom",
                    field,
                    mode: "formula",
                    formula: String(formula).trim(),
                    warehouse_type,
                });
            }
        } else if (option_change === "attribute_clear") {
            onSubmit({
                kind: "custom",
                field: option_change,
                mode: "fixed",
                value: Number(fixedValue),
                warehouse_type,
            });
        } else if (option_change === "title_change") {
            onSubmit({
                kind: "custom",
                field: "title_change",
                mode: "fixed",
                old_value: oldTitle,
                new_value: newTitle,
            });
        } else if (option_change === "package_change") {
            onSubmit({
                kind: "custom",
                field: "package_change",
                mode: "fixed",
                value: {
                    length: Number(lengthValue),
                    width: Number(widthValue),
                    height: Number(heightValue),
                    dimension_unit: dimensionUnit, // CENTIMETER / INCH
                    weight: Number(weightValue),
                    weight_unit: weightUnit, // KILOGRAM / POUND
                },
            });
        } else if (option_change === "feature_tags_change") {
            onSubmit({
                kind: "custom",
                field: "feature_tags_change",
                mode: "fixed",
                value: {
                    product_feature: productFeatures,
                    tags,
                },
            });
        } else {
            onSubmit({
                kind: "template",
                option: option_change,
                templateId,
                warehouse_type,
            });
        }
        onClose();
    };

    return (
        <Fragment>
            <button
                className="btn btn-sm btn-dark me-2 mb-2"
                data-bs-toggle="modal"
                data-bs-target="#kt_modal_create_app"
                id="kt_toolbar_primary_button"
                onClick={() => setIsOpen(true)}
            >
                <i className="ki-outline ki-arrows-circle fs-2"></i>
                {t("Edit Custom")}
            </button>

            <Modal centered size="xl" toggle={onClose} isOpen={isOpen}>
                <div className="modal-header">
                    <h3>Setting</h3>
                    <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
                        <i className="ki-outline ki-cross fs-1" onClick={onClose}></i>
                    </div>
                </div>

                <ModalBody>
                    <h4 className="text-danger mb-5">
                        <i>If you change the price? Please clean the Promotion before doing it.</i>
                    </h4>
                    <h4 className="text-danger mb-5">
                        <i>If changing stock to a new warehouse, please select Warehouse Type as New Warehouse</i>
                    </h4>

                    <div className="row">
                        <div className="col-md-6 form form-group mb-5">
                            <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                <span className="required">{t("Option Edit")}</span>
                            </label>
                            <select
                                className="form form-control"
                                name="option_change"
                                value={option_change}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setOptionChange(v);
                                    setTypeChange("fix");
                                    setFixedValue("");
                                    setFormula("current * 1.10");
                                    setOldTitle("");
                                    setNewTitle("");
                                    setLengthValue("");
                                    setWidthValue("");
                                    setHeightValue("");
                                    setDimensionUnit("CENTIMETER");
                                    setWeightValue("");
                                    setWeightUnit("KILOGRAM");
                                    setProductFeatures([]);
                                    setProductFeatureInput("");
                                    setTags([]);
                                    setTagsInput("");
                                }}
                            >
                                <option value="price_custom">Price Custom</option>
                                <option value="stock_custom">Stock Custom</option>
                                <option value="template_description">Description By Template</option>
                                <option value="template_price">Price By Template</option>
                                <option value="template_stock">Stock By Template</option>
                                <option value="template_variant">Variant By Template</option>
                                <option value="main_images">Main Image Replace</option>
                                <option value="description">Description Replace</option>
                                <option value="description_hashtag">Description Hashtag Replace</option>
                                <option value="attribute_clear">Attribute Clear</option>
                                <option value="template_setting">Set Filter Template (No change)</option>
                                <option value="title_change">Title Change</option>
                                <option value="package_change">Package Change</option>
                                <option value="feature_tags_change">Product Feature & Tags</option>
                                <option value="template_full">Full By Template (In Coming)</option>
                            </select>
                        </div>
                        {option_change === "feature_tags_change" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Product Feature & Tags")}</span>
                                </label>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">{t(`Product Feature (${productFeatures.length}/${MAX_PRODUCT_FEATURE})`)}</label>
                                        <input
                                            className="form form-control"
                                            value={productFeatureInput}
                                            onChange={(e) => setProductFeatureInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addValueToList(productFeatureInput, productFeatures, setProductFeatures, MAX_PRODUCT_FEATURE);
                                                    setProductFeatureInput("");
                                                }
                                            }}
                                            placeholder={t("Enter Product Feature and press Enter")}
                                        />
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {productFeatures.map((item, index) => (
                                                <span
                                                    key={`${item}-${index}`}
                                                    className="badge badge-light-primary d-flex align-items-center"
                                                    style={{ fontSize: "12px", gap: "6px", padding: "8px 10px" }}
                                                >
                                                    {item}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-icon p-0"
                                                        onClick={() => removeValueFromList(index, productFeatures, setProductFeatures)}
                                                        style={{ width: "14px", height: "14px", minHeight: "14px" }}
                                                    >
                                                        <i className="ki-outline ki-cross fs-7"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">{t(`Tags (${tags.length}/${MAX_TAGS})`)}</label>
                                        <input
                                            className="form form-control"
                                            value={tagsInput}
                                            onChange={(e) => setTagsInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    addValueToList(tagsInput, tags, setTags, MAX_TAGS);
                                                    setTagsInput("");
                                                }
                                            }}
                                            placeholder={t("Enter Tag and press Enter")}
                                        />
                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                            {tags.map((item, index) => (
                                                <span
                                                    key={`${item}-${index}`}
                                                    className="badge badge-light-info d-flex align-items-center"
                                                    style={{ fontSize: "12px", gap: "6px", padding: "8px 10px" }}
                                                >
                                                    {item}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-icon p-0"
                                                        onClick={() => removeValueFromList(index, tags, setTags)}
                                                        style={{ width: "14px", height: "14px", minHeight: "14px" }}
                                                    >
                                                        <i className="ki-outline ki-cross fs-7"></i>
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {option_change === "main_images" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Main Images Replace")}</span>
                                </label>

                                <textarea
                                    className="form form-control"
                                    rows={4}
                                    value={mainImages}
                                    onChange={(e) => setMainImages(e.target.value)}
                                    placeholder={`https://image1.jpg\nhttps://image2.jpg`}
                                />

                                {/* PREVIEW – optional */}
                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    {mainImages
                                        .split("\n")
                                        .map(i => i.trim())
                                        .filter(Boolean)
                                        .map((url, idx) => (
                                            <img
                                                key={idx}
                                                src={url}
                                                alt=""
                                                style={{
                                                    width: 70,
                                                    height: 70,
                                                    objectFit: "cover",
                                                    border: "1px solid #ddd",
                                                    borderRadius: 4,
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = "none";
                                                }}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}

                        {option_change === "description" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Product description")}</span>
                                </label>
                                {/* ... phần description giữ nguyên như code cũ của bạn ... */}
                                <JoditEditor
                                    ref={editor}
                                    value={description}
                                    config={config}
                                    tabIndex={1}
                                    onBlur={(newDescription) => setDescription(newDescription)}
                                    onChange={() => { }}
                                    maxHeight={200}
                                />
                            </div>
                        )}

                        {option_change === "description_hashtag" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Hashtag Replace")}</span>
                                </label>
                                <div>
                                    e.g. <b>{`#cotik #tiktok #shop`}</b>
                                </div>
                                <textarea
                                    className="form form-control"
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="e.g. #cotik #tiktok #shop"
                                ></textarea>
                            </div>
                        )}

                        {/* title_change UI */}
                        {option_change === "title_change" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Title Change")}</span>
                                </label>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            {t("Old value")}
                                            <span className="text-danger ms-1">*</span>
                                        </label>
                                        <textarea
                                            className="form form-control"
                                            rows={3}
                                            value={oldTitle}
                                            onChange={(e) => setOldTitle(e.target.value)}
                                            placeholder={t("Enter old title")}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">{t("New value (optional)")}</label>
                                        <input
                                            className="form form-control"
                                            value={newTitle}
                                            onChange={(e) => setNewTitle(e.target.value)}
                                            placeholder={t("Enter new title (can be empty)")}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* package_change UI */}
                        {option_change === "package_change" && (
                            <div className="col-md-12 form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Package Change")}</span>
                                </label>

                                <div className="row g-3 mb-3">
                                    <div className="col-md-3">
                                        <label className="form-label">{t("Length")}</label>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form form-control"
                                            value={lengthValue}
                                            onChange={(e) => setLengthValue(e.target.value)}
                                            placeholder="e.g. 10"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t("Width")}</label>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form form-control"
                                            value={widthValue}
                                            onChange={(e) => setWidthValue(e.target.value)}
                                            placeholder="e.g. 20"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t("Height")}</label>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form form-control"
                                            value={heightValue}
                                            onChange={(e) => setHeightValue(e.target.value)}
                                            placeholder="e.g. 30"
                                        />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="form-label">{t("Dimension Unit")}</label>
                                        <select
                                            className="form form-control"
                                            value={dimensionUnit}
                                            onChange={(e) => handleDimensionUnitChange(e.target.value)}
                                        >
                                            <option value="CENTIMETER">CENTIMETER</option>
                                            <option value="INCH">INCH</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">{t("Weight")}</label>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form form-control"
                                            value={weightValue}
                                            onChange={(e) => setWeightValue(e.target.value)}
                                            placeholder="e.g. 1.5"
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">{t("Weight Unit")}</label>
                                        <select
                                            className="form form-control"
                                            value={weightUnit}
                                            onChange={(e) => handleWeightUnitChange(e.target.value)}
                                        >
                                            <option value="KILOGRAM">KILOGRAM</option>
                                            <option value="POUND">POUND</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="form-text mt-2">
                                    {t("CENTIMETER goes with KILOGRAM, INCH goes with POUND (auto synced).")}
                                </div>
                            </div>
                        )}

                        {(option_change === "stock_custom" ||
                            option_change === "template_stock" ||
                            option_change === "template_variant") && (
                                <div className="col-md-6 form form-group mb-5">
                                    <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                        <span className="required">{t("Warehouse Type")}</span>
                                    </label>
                                    <select
                                        className="form form-control"
                                        name="warehouse_type"
                                        value={warehouse_type}
                                        onChange={(e) => setWarehouseType(e.target.value)}
                                    >
                                        <option value="default">Default</option>
                                        <option value="change">New Warehouse</option>
                                    </select>
                                </div>
                            )}
                    </div>

                    {(option_change === "stock_custom" ||
                        option_change === "price_custom" ||
                        option_change === "attribute_clear") && (
                            <>
                                <div className="form form-group mb-5">
                                    <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                        <span className="required">{t("Type Change")}</span>
                                    </label>
                                    <select
                                        className="form form-control"
                                        name="type_change"
                                        value={type_change}
                                        onChange={(e) => setTypeChange(e.target.value)}
                                    >
                                        <option value="fix">Fixed Value</option>
                                        <option value="custom">Custom Recipe</option>
                                    </select>
                                </div>

                                {type_change === "fix" && (
                                    <div className="form form-group mb-5">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">{t("New Value")}</span>
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            className="form form-control"
                                            value={fixedValue}
                                            onChange={(e) => setFixedValue(e.target.value)}
                                            placeholder={
                                                option_change === "price_custom" ? "e.g. 199000" : "e.g. 120"
                                            }
                                        />
                                    </div>
                                )}

                                {type_change === "custom" && (
                                    <div className="form form-group mb-5">
                                        <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                            <span className="required">{t("Recipe")}</span>
                                        </label>
                                        <div>
                                            <i>
                                                {t("Available variables")}: <b>current</b> ({t("present value")})
                                            </i>
                                        </div>
                                        <div>
                                            <i>
                                                {t("Support")}: <b>+ - * / ( ) %</b> (e.g.{" "}
                                                <code>(current*10)%</code> = <code>current*0.1</code>)
                                            </i>
                                        </div>
                                        <textarea
                                            className="form form-control"
                                            rows={2}
                                            value={formula}
                                            onChange={(e) => setFormula(e.target.value)}
                                            placeholder="current * 1.1"
                                        ></textarea>
                                    </div>
                                )}

                                <div className="form form-group mb-3">
                                    <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                        <span>{t("Preview Current")}</span>
                                    </label>
                                    <div className="row g-3">
                                        <div className="col-12 col-md-4">
                                            <input
                                                type="number"
                                                className="form form-control"
                                                value={previewCurrent}
                                                onChange={(e) => setPreviewCurrent(e.target.value)}
                                                placeholder={t("Enter a current value to preview")}
                                            />
                                        </div>
                                        <div className="col-12 col-md-8 d-flex align-items-center">
                                            <div
                                                className={`w-100 p-3 border rounded ${preview.ok ? "bg-light" : "bg-danger-subtle"
                                                    }`}
                                            >
                                                {preview.ok ? (
                                                    <div>
                                                        <div className="small text-muted">
                                                            {t("Expected result")}:
                                                        </div>
                                                        <div className="fw-semibold fs-6">
                                                            {String(preview.value)}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-danger small">
                                                        {t("Cannot preview — please check value/formula.")}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                    {(option_change === "template_description" ||
                        option_change === "template_price" ||
                        option_change === "template_full" ||
                        option_change === "template_variant" ||
                        option_change === "template_stock" ||
                        option_change === "template_setting") && (
                            <div className="form form-group mb-5">
                                <label className="d-flex align-items-center fs-6 fw-semibold mb-2">
                                    <span className="required">{t("Change By Template")}</span>
                                </label>
                                <SelectBox
                                    className="w-100"
                                    options={templates}
                                    name="templateId"
                                    placeholder="Select template"
                                    value={templateId || ""}
                                    onChange={(data) => setTeamplateId(data.value)}
                                />
                            </div>
                        )}

                    {errors.length > 0 && (
                        <div className="alert alert-danger" role="alert">
                            <ul className="mb-0">
                                {errors.map((e, i) => (
                                    <li key={i}>{e}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <button
                        type="reset"
                        id="kt_modal_users_search_reset"
                        data-bs-dismiss="modal"
                        className="btn btn-active-light me-3"
                        onClick={onClose}
                    >
                        {t("Cancel")}
                    </button>
                    <button
                        onClick={handleSubmit}
                        type="submit"
                        id="kt_modal_users_search_submit"
                        className="btn btn-primary"
                        disabled={!canSave}
                    >
                        {t("Save")}
                    </button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
}

const mapStateToProps = (state) => ({});
const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(ModalUpload);
